
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface LocationConsiderations {
  economic: string;
  demographic: string;
  cultural: string;
  political: string;
  market: string;
  technological: string;
  barriers: string;
}

export const savePlaceData = async (
  addressValue: string, 
  considerations: LocationConsiderations, 
  placeId: string | null,
  setPlaceId: (id: string) => void
) => {
  try {
    const hasEmptyValues = Object.values(considerations).some(
      value => !value || value === 'Loading...' || value === 'Analysis unavailable'
    );
    
    if (hasEmptyValues) {
      console.log('Not saving incomplete considerations data to Supabase');
      return;
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.error('User not authenticated');
      return;
    }

    if (placeId) {
      const { error } = await supabase
        .from('place')
        .update({
          address: addressValue,
          economic_env: considerations.economic,
          demographics: considerations.demographic,
          cultural_social: considerations.cultural,
          political_legal: considerations.political,
          market_dynamics: considerations.market,
          technology: considerations.technological,
          barriers: considerations.barriers,
          updated_at: new Date().toISOString()
        })
        .eq('id', placeId);

      if (error) {
        console.error('Error updating place data:', error);
        toast.error('Failed to update location analysis');
      } else {
        console.log('Place data updated successfully');
      }
    } else {
      const { data, error } = await supabase
        .from('place')
        .insert({
          user_id: user.id,
          address: addressValue,
          economic_env: considerations.economic,
          demographics: considerations.demographic,
          cultural_social: considerations.cultural,
          political_legal: considerations.political,
          market_dynamics: considerations.market,
          technology: considerations.technological,
          barriers: considerations.barriers
        })
        .select();

      if (error) {
        console.error('Error saving place data:', error);
        toast.error('Failed to save location analysis');
      } else if (data && data.length > 0) {
        setPlaceId(data[0].id);
        console.log('Place data saved successfully');
      }
    }
  } catch (error) {
    console.error('Error in savePlaceData:', error);
    toast.error('Failed to save location analysis');
  }
};

export const loadPlaceData = async (
  setPlaceId: (id: string) => void,
  setAddress: (address: string) => void,
  setLocationConsiderations: (considerations: LocationConsiderations) => void,
  setDataLoaded: (loaded: boolean) => void,
  setIsLoadingPlace: (loading: boolean) => void
) => {
  try {
    setIsLoadingPlace(true);
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      console.error('User not authenticated');
      setIsLoadingPlace(false);
      return;
    }

    const { data, error } = await supabase
      .from('place')
      .select('*')
      .eq('user_id', user.id)
      .order('updated_at', { ascending: false })
      .limit(1);

    if (error) {
      console.error('Error loading place data:', error);
      toast.error('Failed to load location analysis');
    } else if (data && data.length > 0) {
      const placeData = data[0];
      setPlaceId(placeData.id);
      setAddress(placeData.address);

      setLocationConsiderations({
        economic: placeData.economic_env || 'No data available',
        demographic: placeData.demographics || 'No data available',
        cultural: placeData.cultural_social || 'No data available',
        political: placeData.political_legal || 'No data available',
        market: placeData.market_dynamics || 'No data available',
        technological: placeData.technology || 'No data available',
        barriers: placeData.barriers || 'No data available'
      });
      
      setDataLoaded(true);
      console.log('Place data loaded successfully');
    } else {
      setDataLoaded(true);
    }
  } catch (error) {
    console.error('Error in loadPlaceData:', error);
  } finally {
    setIsLoadingPlace(false);
  }
};
